import React, { useState, useCallback } from 'react';
import { Modal, Radio, Row, Col, Input, Button, Tooltip } from "antd";
import { noop, get } from 'lodash';
import {useTranslation} from 'react-i18next'
import DoubleArrowImage from 'src/assets/img/double-arrow.svg';
import { filterObjectsArrayByProperty } from 'src/utils/table-util';
import PeopleItem from 'src/components/people-item';
import NeedsItem from 'src/components/need-item';
import FormItem from 'src/components/form-item';
import ConfirmFitModal from '../confirm-fit-modal';
import UndoFitReasonModal from 'src/components/undo-fit-reason-modal';
import { FitPeopleLabel } from 'src/components/fit-people-label';
import { filteredAndRejected } from 'src/utils/helpers';
import {
    UndoOutlined,
} from "@ant-design/icons";
import './style.scss';

const { Search } = Input;

/**
 * Retrieves the needs with suggestions based on the provided needs and deallocation data.
 * @param {Array} needs - The needs array.
 * @param {Object} deallocation - The deallocation object.
 * @returns {Array} The needs with suggestions.
 */
const getNeedsWithSuggestions = (needs, deallocation) => {
    const suggestionNeeds = deallocation.deallocation_suggestion.map(suggestion => {
        const dataNeed = needs.find(need => need.requisitionCode === suggestion.requisitionCode);
        if (dataNeed) {
            return {
                ...suggestion,
                ...dataNeed,
            }
        }

        return null;
    })

    return suggestionNeeds.filter(filteredNeed => filteredNeed !== null);
}

/**
 * Renders a fit modal component.
 * @param {Object} props - The component props.
 * @param {Object} props.deallocation - The deallocation object.
 * @param {Object} props.user - The logged user object.
 * @param {Function} props.onClose - The function triggered when closing the modal or canceling the action.
 * @param {Array} props.needs - The array of Needs' objects.
 * @param {Function} props.onSave - The function to save the fit.
 * @param {Function} props.onCancelFit - The function to cancel the fit.
 * @param {Boolean} props.loading - Indicates if the fit modal is in a loading state.
 * @param {Array} props.positionMaps - The position maps array.
 * @returns {JSX.Element} The rendered fit modal component.
 * @example
 *  <FitModal
        deallocation={matching}
        onClose={() => setMatching(null)}
        needs={needs}
        onSave={createFit}
        onCancelFit={cancelFit}
        loading={formFetching}
        positionMaps={positionMaps}
        user={user}
    />
 */
function FitModal({ deallocations, deallocation, user, onClose, needs, onSave, onCancelFit, loading, positionMaps }) {
    const [undoFitId, setUndoFitId] = useState(null);
    const [undoFitLoading, setUndoFitLoading] = useState(false);
    const deallocationId = [deallocation.id]; // send an array of deallocations, even if it's only one
    const deallocationNeeds = get(deallocation, 'needs', []); // needs from deallocations' association
    const nonMatchNeeds = needs.filter(need => (!need.deallocations
        .some(deallocation => deallocation?.Fits?.match === 'Y')
    )); // get all needs without a match
    const optMatchNeeds = getNeedsWithSuggestions(nonMatchNeeds, deallocation); // get opt match needs
    const fitsPeopleNeedId = deallocationNeeds
        .filter((need) => need.fits.active === 'Y' && need.fits.match === 'N')
        .map((need) => need.id); // needIds from needs with fits (pending match)
    const activePeopleFitsId = deallocation.needs
        .filter((need) => need.fits.active === 'Y' && need.fits.match === 'N')
        .map((need) => need.fits.id); //fitIds from needs with fits
    const fitResponsibleIds = deallocation.needs.map(need => need.fits.responsibleId) // fitResponsibleIds from needs with fits

    const [ needId, setNeedId ] = useState(null);
    const [ filter, setFilter ] = useState('');
    const [needsId, setNeedsId] = useState(fitsPeopleNeedId);
    const [isFiltered, setIsFiltered] = useState(true);
    const [isConfirming, setIsConfirming] = useState(null)
    const [fitData, setFitData] = useState([])

    const { t } = useTranslation()

    /**
     * Update needId upon selection.
     */
    const onChangeNeed = useCallback(
        (needId) => {
            setNeedsId([needId]);
        },
        [setNeedsId]
    );

    /**
     * Organize list of needs and sort them, in this order:
     * - optMatchFitNeeds: fit and opt match suggestion.
     * - otherFits: only fit.
     * - optMatchNonFitNeeds: not a fit, but an opt match suggestion.
     * - otherNonFits: neither a fit nor an opt match suggestion.
     */
    const orderedResult = (() => {
        const [fitsPeople, fits] = filteredAndRejected(nonMatchNeeds, item => fitsPeopleNeedId.includes(item.id));

        const compare = (a, b) => a.title.localeCompare(b.title);

        const compareOptMatch = (a, b) => b.optMatchPercent.localeCompare(a.optMatchPercent);

        fitsPeople.forEach(fit => fit.isFitNeed = true);
        fitsPeople.sort(compare);

        // fits with opt match
        const optMatchFitNeeds = getNeedsWithSuggestions(fitsPeople, deallocation);
        optMatchFitNeeds.sort(compareOptMatch);

        // fits without opt match
        const otherFits = fitsPeople.filter(filterNeed => !optMatchNeeds.find(need => need.requisitionCode === filterNeed.requisitionCode));
        otherFits.sort(compare);

        // avoid repeating optMatch suggestions with others
        const otherNonFits = fits.filter(
            (filterNeed) => !optMatchNeeds.find((need) => need.id === filterNeed.id),
        )
        otherNonFits.sort(compare);

        // if opt match suggestion is already a fit, avoid returning it twice
        const optMatchNonFitNeeds = optMatchNeeds.filter(
          (filterNeed) =>
            !fitsPeople.find((need) => need.requisitionCode === filterNeed.requisitionCode),
        );
        optMatchNonFitNeeds.sort(compareOptMatch);

        return [...optMatchFitNeeds, ...otherFits, ...optMatchNonFitNeeds, ...otherNonFits];
    })();

    /**
     * Change list of needs upon changing filters input value.
     */
    const resultNeeds = filterObjectsArrayByProperty(
        orderedResult,
        [
            'category',
            'client',
            'description',
            'englishLevel',
            'id',
            'level',
            'requisitionCode',
            'requisitionTalentAttraction',
            'requisitionTalentAttractionEmail',
            'role',
            'site',
            'team',
            'technology',
            'title',
            'workType',
        ],
        filter
    );

    /**
     * Retrieves the selected position maps based on the deallocation and position maps data.
     * 
     * @param {Object} deallocation - The deallocation object.
     * @param {Array} positionMaps - The position maps array.
     * @returns {Array} The selected position maps.
     */
    const getSelectedPositionMaps = (deallocation, positionMaps) => {
        const deallocationValue = deallocation.collaborator_deallocation.positionMap;
        const currentIndex = positionMaps.findIndex(item => item.value[0] === deallocationValue[0]); // compare their numbers
      
        const selectedPositionMaps = [];
        if (currentIndex !== -1) {
            selectedPositionMaps.push(positionMaps[currentIndex]);
            if (currentIndex < positionMaps.length - 1) {
                selectedPositionMaps.push(positionMaps[currentIndex + 1]);
            }
        }
      
        return selectedPositionMaps;
    }

    /**
     * Iterate over list of needs and render them in radio options.
     */
    const needsOptions = resultNeeds
        .filter(need => {
            if (isFiltered) {
                const selectedPositionMaps = getSelectedPositionMaps(deallocation, positionMaps);
                if ((need.optMatchPercent && need.matchDateSuggestion) || need.isFitNeed) {
                    // no filters applied to opt match suggestions or needs that are fits
                    return need;
                }
    
                return need.active && need.role === deallocation.collaborator_deallocation.role &&
                 selectedPositionMaps.some(positionMap => positionMap.value[0] === need.level[0]); // compare by positionMap number
            }

            return need;
        })
        .map(need => {
            const isPeopleSuggestion = fitsPeopleNeedId.includes(need.id);
            const deallocationNeed = deallocationNeeds.find(dn => dn.id === need.id);
            const activeFitId = deallocationNeed?.fits?.id ?? null;
            const fitResponsibleId = deallocationNeed?.fits?.responsibleId ?? null;
            let labelType = '';
			if (isPeopleSuggestion) {
				labelType = 'fit';
                if (need.optMatchPercent) labelType = 'fit-opt-match';
			} else {
                if (need.optMatchPercent) labelType = 'opt-match';
            };
            return (
                <React.Fragment key={need.id}>
                    {isPeopleSuggestion ? (
                        <FitPeopleLabel type={labelType} people={'people'}>
                            <div className={`fit-people ${labelType}`}>
                              {fitResponsibleId === user.id && (
                                <Tooltip>
                                    <div className='profile-fit__undo'>
                                    <Button block icon={<UndoOutlined />} onClick={() => setUndoFitId(activeFitId)} className='profile-fit__undo_button'
                                    aria-label={t('undoFit')}>
                                        <span className='profile-fit__undo_text'>{t('undoFit')}</span>
                                    </Button>
                                    </div>
                                </Tooltip>)}
                                <NeedsItem need={need}/>
                            </div>
                        </FitPeopleLabel>
                    ) : (
                        <FitPeopleLabel type={labelType} people={'people'}>
                            <Radio value={need.id} onChange={(e) => onChangeNeed(need.id)} 
                                className={`fit-radio fit-radio-people ${labelType === '' ? 'add-margin-non-fit' : ''}`}
                                aria-label={t('selectFit', {type: t('needsTitle'), title: need.title})}>
                                <span className='profile-fit__text'>Fit</span>
                                <NeedsItem need={need} />
                            </Radio>
                        </FitPeopleLabel>
                    )}
                </React.Fragment>
            );
        });

    /**
     * Call onSave method upon saving fit.
     */
    const onOk = useCallback(() => {
        setIsConfirming(true)
        setFitData({deallocation: deallocation, need: needs.find(need => need.id === needsId[0])})
    }, [deallocation, needs, needsId]);

    const onConfirmFit = useCallback(() => {
        onSave(deallocationId, needsId);
    }, [onSave, deallocationId, needsId])

    /**
     * Call onClose method upon canceling action or closing modal.
     */
    const onCancel = useCallback(() => {
        if (loading) {
            return;
        }

        onClose();
    }, [ loading, onClose ]);

    /**
     * Returns a string indicating whether a deallocation has an active need.
     *
     * @param {Object} deallocation - The deallocation object to check.
     * @param {Array} deallocation.needs - An array of needs associated with the deallocation.
     * @param {Function} t - The translation function to use.
     * @returns {string} A string indicating whether the deallocation has an active need.
     */

    if (undoFitId) {
      return (
        <UndoFitReasonModal
          onCancel={() => setUndoFitId(null)}
          onConfirm={async (cancelCategory, cancelSubcategory) => {
            setUndoFitLoading(true);
            try {
              await onCancelFit(undoFitId, cancelCategory, cancelSubcategory);
            } finally {
              setUndoFitLoading(false);
              setUndoFitId(null);
            }
          }}
          loading={undoFitLoading}
        />
      );
    }

    return isConfirming ? (
      <ConfirmFitModal
        onConfirm={onConfirmFit}
        fitData={fitData}
        deallocations={deallocations}
        type="people"
        onCancel={() => setIsConfirming(false)}
        loading={loading}
      />
    ) : (
      <Modal
        title={t('suggestFit')}
        width={1000}
        className="profile-fit"
        visible={true}
        confirmLoading={loading}
        onCancel={onCancel}
        onOk={onOk}
        okButtonProps={{
          className: 'cit-btn primary-type',
          disabled: !needId,
        }}
        cancelButtonProps={{
          className: 'cit-btn secondary-type link-type cancel-button',
        }}
        cancelText={t('back')}
        okText={t('confirmFit')}
        aria-modal="true"
      >
        <div className="profile-fit__subtitle">
          <Row className="filters" align="top">
            <Col span={14}>
              <div
                className="profile-fit__subtitle_gutter"
                role="note"
                tabIndex={0}
              >
                {t('listRightAllNeeds')}
              </div>
              <FormItem label={t('filter')} id='fit-filter'>
                <Search
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  aria-label={t('modalFitFilter')}
                  aria-labelledby='fit-filter'
                  role="searchbox"
                />
              </FormItem>
            </Col>
          </Row>
          <Row className="profile-radio__filter" align="bottom">
            <Col span={14}>
              <Radio.Group
                name="radiogroup"
                value={isFiltered}
                onChange={(e) => setIsFiltered(e.target.value)}
              >
                {/* <Space direction='vertical' > */}
                <div className="profile-radio__filter_align">
                  <Radio value={true}>
                    <span className="profile-radio__filter_text">
                      {t('filteredOpportunitiesOnly')}
                    </span>
                  </Radio>
                  <Radio value={false}>
                    <span className="profile-radio__filter_text">
                      {t('allOpportunities')}
                    </span>
                  </Radio>
                </div>

                {/* </Space> */}
              </Radio.Group>
            </Col>
          </Row>
        </div>

        <div className="profile-fit__container">
          <FormItem label={t('profile')}>
            <div className="profile-fit__container_profiles">
              <div className="profile-fit__container_options">
                <PeopleItem
                  people={deallocation}
                  className={'is-people-page'}
                />
              </div>
            </div>
          </FormItem>
          <div className="profile-fit__container_arrow">
            <img
              src={DoubleArrowImage}
              alt="match arrow"
              className="profile-fit__container_arrow_size"
            />
          </div>
          <div className="profile-fit__container_options">
            {needsOptions.length > 0 ? (
              <Radio.Group
                value={needId}
                onChange={(e) => setNeedId(e.target.value)}
              >
                {needsOptions}
              </Radio.Group>
            ) : (
              <p style={{ textAlign: 'center' }}>
                {t('noMatchingOpportunities')}
              </p>
            )}
          </div>
        </div>
      </Modal>
    );
}

FitModal.defaultProps = {
    needs: [],
    onClose: noop,
    onSave: noop,
    loading: false
};

export default FitModal;
